import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import Add from '@mui/icons-material/Add'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Title from '../../component/Title'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListActionGroup from '../../component/ListActionGroup'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import { BUTTON_LOADING_ICON, FORM_DIALOG_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { LIST_ACTION_EDIT_BUTTON_SX } from '../../component/listActionIconStyles'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import {
    MEDAL_PORTFOLIO_IMAGE_LEAVE_MESSAGE,
    hasPendingMedalPortfolioImage,
    usePendingMedalPortfolioImageUnloadGuard,
} from './medalPortfolioLeaveGuard'

const sortActionButtonSx = {
    ...LIST_ACTION_EDIT_BUTTON_SX,
    color: '#475569',
}

const MEDAL_PORTFOLIO_IMAGE_FILE_ACCEPT = 'image/*'
const MEDAL_PORTFOLIO_IMAGE_FILE_ERROR_MESSAGE = '이미지 파일만 업로드할 수 있습니다.'

// 메달 포트폴리오 모달에서 이미지 파일만 선택되도록 저장 전 기준을 맞춘다.
const isMedalPortfolioImageFile = (file) => {
    return Boolean(file) && Boolean(file.type) && file.type.startsWith('image/')
}

export default function MedalPortfolios({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const actionInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: {
            searchStatus: '',
        },
        selectParams: {
            searchStatus: 'searchStatus',
        },
        getExtraState: ({ locationState, searchParams }) => ({
            initialModalMode: searchParams.get('modal') || locationState.modalMode || '',
            initialEditId: searchParams.has('portfolioId')
                ? parseNumberParam(searchParams.get('portfolioId'), null)
                : (locationState.editId || null),
            initialDraftTitle: searchParams.has('draftTitle')
                ? (searchParams.get('draftTitle') || '')
                : (locationState.title ?? null),
        }),
    }))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [saving, setSaving] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [lastSort, setLastSort] = useState(0)
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const searchTextRef = useRef(initialListState.searchText)
    const [searchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [openDialog, setOpenDialog] = useState(() => initialListState.initialModalMode === 'new' || Boolean(initialListState.initialEditId))
    const [modalMode, setModalMode] = useState(() => initialListState.initialEditId ? 'edit' : (initialListState.initialModalMode === 'new' ? 'new' : ''))
    const [title, setTitle] = useState(initialListState.initialDraftTitle || '')
    const [image, setImage] = useState(null)
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
    const [editId, setEditId] = useState(initialListState.initialEditId)
    const leaveGuardEnabled = openDialog && hasPendingMedalPortfolioImage(image) && !saving

    usePendingMedalPortfolioImageUnloadGuard(leaveGuardEnabled)
    
    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/medal/getPortfolioList', {
                page: curPage, offset: offset, searchText: searchTextRef.current, searchSelect: searchSelect
            })
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setLastSort(data.data.lastSort || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '메달 포트폴리오 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setLastSort(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '메달 포트폴리오 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setLastSort(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery: searchTextRef.current,
            searchSelect,
            selectParams: {
                searchStatus: 'searchStatus',
            },
            extraParams: [
                {
                    param: 'modal',
                    value: modalMode,
                    shouldInclude: () => openDialog && (modalMode === 'new' || modalMode === 'edit'),
                },
                {
                    param: 'portfolioId',
                    value: editId,
                    shouldInclude: () => openDialog && modalMode === 'edit' && Boolean(editId),
                },
                {
                    param: 'draftTitle',
                    value: title,
                    shouldInclude: () => openDialog && (modalMode === 'new' || modalMode === 'edit'),
                },
            ],
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, editId, history, location.pathname, location.search, location.state, modalMode, openDialog, searchSelect, title])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading) || saving

    // 정렬 관리 화면은 조회 실패와 데이터 없음만 구분해 안내한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="메달 포트폴리오 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 메달 포트폴리오가 없습니다."
                description="메달 포트폴리오를 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const runAction = async ({ key, endpoint, payload, errorMessage, onSuccess }) => {
        if (actionInFlightRef.current.has(key) || isBusy) {
            return
        }

        try {
            actionInFlightRef.current.add(key)
            setActionLoading(key)

            const { data } = await API.post(endpoint, payload)
            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
                if (onSuccess) {
                    await onSuccess()
                } else {
                    await getData(false)
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            actionInFlightRef.current.delete(key)
            setActionLoading(null)
        }
    }

    const handleSortUpdate = async (medalPortfolioId, sort, type) => {
        await runAction({
            key: `sort:${medalPortfolioId}:${type}`,
            endpoint: '/api/v1/admin/medal/sortUpdatePortfolio',
            payload: { medalPortfolioId, sort, type },
            errorMessage: '메달 포트폴리오 순서를 변경하지 못했습니다.'
        })
    }

    const handleDelete = async (id) => {
        await runAction({
            key: `delete:${id}`,
            endpoint: '/api/v1/admin/medal/deletePortfolio',
            payload: { medalPortfolioId: id },
            errorMessage: '메달 포트폴리오를 삭제하지 못했습니다.',
            onSuccess: async () => {
                if (curPage > 1 && params.length === 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            }
        })
    }

    const resetModalState = () => {
        if (saving) {
            return
        }
        setTitle('')
        setImage(null)
        setImagePreviewUrl(null)
        setEditId(null)
        setModalMode('')
        setOpenDialog(false)
    }

    const closeDialog = (event) => {
        if (saving) {
            return
        }

        if (leaveGuardEnabled) {
            event?.preventDefault?.()

            if (!window.confirm(MEDAL_PORTFOLIO_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        resetModalState()
    }

    // 수정 모달은 query 기준으로도 다시 열 수 있게 편집 대상 id를 함께 유지한다.
    const handleEdit = (item) => {
        if (isBusy) {
            return
        }
        setTitle(item.title || '')
        setImage(null)
        setImagePreviewUrl(item.image ? `${process.env.REACT_APP_AWS_S3_URL}/${item.image}` : null)
        setEditId(item.medalPortfolioId)
        setModalMode('edit')
        setOpenDialog(true)
    }

    const handleCreate = () => {
        if (isBusy) {
            return
        }

        setTitle('')
        setImage(null)
        setImagePreviewUrl(null)
        setEditId(null)
        setModalMode('new')
        setOpenDialog(true)
    }

    // 수정 모달은 새로고침 후에도 draft 타이틀이 있으면 그 값을 우선 복원한다.
    useEffect(() => {
        if (!openDialog || modalMode !== 'edit' || !editId) {
            return
        }

        const targetItem = params.find((item) => Number(item.medalPortfolioId) === Number(editId))

        if (!targetItem) {
            setModalMode('')
            setEditId(null)
            setOpenDialog(false)
            return
        }

        setTitle(initialListState.initialDraftTitle ?? (targetItem.title || ''))
        setImage(null)
        setImagePreviewUrl(targetItem.image ? `${process.env.REACT_APP_AWS_S3_URL}/${targetItem.image}` : null)
    }, [editId, initialListState.initialDraftTitle, modalMode, openDialog, params])

    const handleSubmit = async () => {
        if (isBusy) {
            return
        }

        const nextTitle = (title || '').trim()

        if (editId) {
            if (!nextTitle) {
                setMessageSnackbar("타이틀을 입력해주세요.")
                setSeveritySnackbar("warning")
                setOpenSnackbar(true)
                return
            }
        } else {
            if (!image || !nextTitle) {
                setMessageSnackbar("이미지와 타이틀을 모두 입력해주세요.")
                setSeveritySnackbar("warning")
                setOpenSnackbar(true)
                return
            }
        }

        try {
            setSaving(true)
            let url = 'createPortfolio'
            const formData = new FormData()
            formData.append("title", nextTitle)

            if (image) formData.append('image', image)

            if (editId) {
                formData.append('medalPortfolioId', editId)
                url = 'updatePortfolio'
            }

            const { data } = await API.post(`/api/v1/admin/medal/${url}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            if (data.code === 200) {
                setTitle('')
                setImage(null)
                setImagePreviewUrl(null)
                setEditId(null)
                setModalMode('')
                setOpenDialog(false)
                setOpenSnackbar(true)
                setSeveritySnackbar("success")
                setMessageSnackbar(editId ? "수정되었습니다." : "등록되었습니다.")
                await getData(false)
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar("error")
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            console.error(err)
            setOpenSnackbar(true)
            setSeveritySnackbar("error")
            setMessageSnackbar("메달 포트폴리오를 저장하지 못했습니다.")
        } finally {
            setSaving(false)
        }
    }

    useEffect(() => {
        return () => {
            if (imagePreviewUrl) {
                URL.revokeObjectURL(imagePreviewUrl)
            }
        }
    }, [imagePreviewUrl])

    return (
        <Grid item xs={12}>
            <Prompt when={leaveGuardEnabled} message={MEDAL_PORTFOLIO_IMAGE_LEAVE_MESSAGE} />
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 5, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>메달 포트폴리오 관리</Title>
                    <ListTableToolbar
                        right={(
                            <Button
                                onClick={handleCreate}
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                신규추가
                            </Button>
                        )}
                    />
                    {/* 테이블 */}
                    <ListTableContainer sx={{ opacity: isBusy ? 0.6 : 1, pointerEvents: isBusy ? 'none' : 'auto' }}>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>노출순서</TableCell>
                                    <TableCell>이미지</TableCell>
                                    <TableCell>정렬</TableCell>
                                    <TableCell align="center">타이틀</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.medalPortfolioId}>
                                            <TableCell>{row.sort}</TableCell>
                                            {/* <TableCell align="center">{row.image}</TableCell> */}
                                            <TableCell><img src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`} alt="" width={50} /></TableCell>
                                            <TableCell>
                                                <ListActionGroup sx={{ justifyContent: 'flex-start' }}>
                                                    {row.sort > 1 && (
                                                        <Tooltip title="Move up">
                                                            <Box
                                                                component="button"
                                                                type="button"
                                                                onClick={() => handleSortUpdate(row.medalPortfolioId, row.sort, 'up')}
                                                                aria-label="Move up"
                                                                disabled={isBusy}
                                                                sx={{
                                                                    ...sortActionButtonSx,
                                                                    cursor: isBusy ? 'default' : 'pointer',
                                                                    opacity: isBusy ? 0.5 : 1,
                                                                    color: isBusy ? 'rgba(100, 116, 139, 0.42)' : sortActionButtonSx.color,
                                                                }}
                                                            >
                                                                <ArrowUpward />
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                    {lastSort !== row.sort && (
                                                        <Tooltip title="Move down">
                                                            <Box
                                                                component="button"
                                                                type="button"
                                                                onClick={() => handleSortUpdate(row.medalPortfolioId, row.sort, 'down')}
                                                                aria-label="Move down"
                                                                disabled={isBusy}
                                                                sx={{
                                                                    ...sortActionButtonSx,
                                                                    cursor: isBusy ? 'default' : 'pointer',
                                                                    opacity: isBusy ? 0.5 : 1,
                                                                    color: isBusy ? 'rgba(100, 116, 139, 0.42)' : sortActionButtonSx.color,
                                                                }}
                                                            >
                                                                <ArrowDownward />
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                </ListActionGroup>
                                            </TableCell>
                                            <TableCell className="listTableLimitText">{row.title}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup>
                                                    <Tooltip title="Edit">
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            onClick={() => handleEdit(row)}
                                                            aria-label="Edit"
                                                            disabled={isBusy}
                                                            sx={{
                                                                ...LIST_ACTION_EDIT_BUTTON_SX,
                                                                cursor: isBusy ? 'default' : 'pointer',
                                                                opacity: isBusy ? 0.5 : 1,
                                                                color: isBusy ? 'rgba(100, 116, 139, 0.42)' : LIST_ACTION_EDIT_BUTTON_SX.color,
                                                            }}
                                                        >
                                                            <ModeEditIcon />
                                                        </Box>
                                                    </Tooltip>
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.medalPortfolioId} disabled={isBusy}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {/* {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />} */}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}

                    <Dialog open={openDialog} onClose={saving ? undefined : closeDialog} maxWidth="sm" fullWidth>
                        <DialogTitle>메달 포트폴리오 신규 {editId ? '수정' : '등록'}</DialogTitle>
                        <DialogContent>
                            <Box display="flex" flexDirection="column" gap={2} mt={1}>
                                <TextField
                                    label="타이틀"
                                    variant="outlined"
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    disabled={saving}
                                    fullWidth
                                />
                                <FormControl fullWidth>
                                    <TextField
                                        inputProps={{ accept: MEDAL_PORTFOLIO_IMAGE_FILE_ACCEPT }}
                                        style={{ display: 'none' }}
                                        name="image"
                                        id="medalPortfolioImage"
                                        type="file"
                                        disabled={saving}
                                        onChange={(e) => {
                                            const file = e.target.files[0]
                                            if (file) {
                                                if (!isMedalPortfolioImageFile(file)) {
                                                    setOpenSnackbar(true)
                                                    setSeveritySnackbar("warning")
                                                    setMessageSnackbar(MEDAL_PORTFOLIO_IMAGE_FILE_ERROR_MESSAGE)
                                                    return
                                                }
                                                setImage(file)
                                                setImagePreviewUrl(URL.createObjectURL(file))
                                            }
                                        }}
                                    />
                                    <Box component="label" htmlFor="medalPortfolioImage" sx={{ display: 'block' }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="success"
                                            component="span"
                                            startIcon={<Add />}
                                            sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                                            disabled={saving}
                                        >
                                            이미지 선택
                                        </Button>
                                    </Box>
                                </FormControl>
                                {image && (
                                    <Box component="p" sx={{ mt: 1, mb: 0, fontSize: '0.75rem', color: 'text.secondary' }}>
                                        선택한 파일: {image.name}
                                    </Box>
                                )}
                            </Box>
                            {imagePreviewUrl && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        width: '100%',
                                        height: { xs: 220, md: 300 },
                                        borderRadius: '4px',
                                        border: '1px solid rgba(148, 163, 184, 0.24)',
                                        backgroundColor: '#f8fafc',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={imagePreviewUrl}
                                        alt="미리보기"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX} disabled={saving}>취소</Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                color="primary"
                                sx={FORM_DIALOG_ACTION_BUTTON_SX}
                                disabled={saving}
                                startIcon={saving ? BUTTON_LOADING_ICON : undefined}
                                aria-busy={saving}
                            >
                                {saving ? '저장 중...' : '저장'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            )}
        </Grid>
    )
}
